import { buttonStyle } from 'components/buttons/buttonStyle';
import PermissionButton from 'components/buttons/PermissionButton.tsx';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import { FormattedDate } from 'components/misc/FormattedDate.tsx';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput.tsx';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput.tsx';
import GridItemList from 'components/GridItemList.tsx';
import { ExpandMore as ExpandMoreIcon } from 'components/icons';
import { useModal } from 'components/modals/useModal';
import { RecordLink } from 'components/record-chip/RecordLink';
import MoneyDisplay from 'components/util/MoneyDisplay.tsx';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Tooltip } from 'components/ui/tooltip';
import { useCurrentEmployee } from 'pages/employees/useCurrentEmployee.ts';
import React, { ReactNode, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useUpdateBankTransferMutation } from 'store/api/bank-transfers.ts';
import { BankTransactionDetails, BankTransactionUpdateDto } from 'types/bank-transfers.ts';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations.ts';
import { logError } from 'utils/monitoring';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import BankTransactionNotesModal from './BankTransactionNotesModal.tsx';
import BankTransferConnectInvoices from './BankTransferConnectInvoices.tsx';

type BankTransferBalanceProps = {
    bankTransfer: BankTransactionDetails;
    onBankTransferUpdated: (details: BankTransactionDetails) => void;
};

const BankTransferBalance: React.FC<BankTransferBalanceProps> = ({
    bankTransfer: initialBankTransfer,
    onBankTransferUpdated,
}) => {
    const { openModal, closeModal } = useModal();
    const [updateBankTransfer] = useUpdateBankTransferMutation();
    const isProcessedOutside = initialBankTransfer.isProcessedOutside;
    const isProcessedOutsideDisabled = initialBankTransfer.isProcessedOutsideDisabled;
    const isNonInvoiceTransactionDisabled = initialBankTransfer.isNonInvoiceTransactionDisabled;
    const isConnectInvoiceDisabled = initialBankTransfer.isConnectInvoiceDisabled;
    const [isProcessedOutsideBoxDisplayed, setIsProcessedOutsideBoxDisplayed] = useState<boolean>(false);
    const { data: currentEmployee } = useCurrentEmployee();
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const isInViewMode = !useHasPermissions(['UPDATE.BANK_TRANSACTIONS.ALL', 'UPDATE.BANK_TRANSACTIONS.MY_COMPANY']);

    const { handleSubmit, control } = useForm<BankTransactionDetails>({
        defaultValues: initialBankTransfer,
    });

    const handleOnProcessedOutsideChange = async (formData: BankTransactionDetails) => {
        const dto: BankTransactionUpdateDto = {
            id: initialBankTransfer.id,
            amountNoninvoice: formData.amountNoninvoice,
            noninvoiceNotes: formData.noninvoiceNotes,
            isNonInvoiceTransaction: initialBankTransfer.isNonInvoiceTransaction,
            invoicePayments: initialBankTransfer.invoicePayments.map((bt) => {
                return {
                    invoicePaymentId: bt.id,
                    invoiceId: bt.invoice.id,
                    amountInTransactionCurrency: bt.amountInTransactionCurrency,
                };
            }),
        };
        await updateBankTransfer(dto)
            .unwrap()
            .then((resp) => {
                onBankTransferUpdated(resp);
                setIsProcessedOutsideBoxDisplayed(false);
            })
            .catch((error) => {
                console.error(error);
                logError(error);
            });
    };

    const handleOnNonInvoiceTransactionChange = async (formData: BankTransactionDetails) => {
        const dto: BankTransactionUpdateDto = {
            id: initialBankTransfer.id,
            amountNoninvoice: initialBankTransfer.amountNoninvoice,
            noninvoiceNotes: initialBankTransfer.noninvoiceNotes,
            isNonInvoiceTransaction: formData.isNonInvoiceTransaction,
            invoicePayments: initialBankTransfer.invoicePayments.map((bt) => {
                return {
                    invoicePaymentId: bt.id,
                    invoiceId: bt.invoice.id,
                    amountInTransactionCurrency: bt.amountInTransactionCurrency,
                };
            }),
        };
        await updateBankTransfer(dto)
            .unwrap()
            .then((resp) => {
                onBankTransferUpdated(resp);
            })
            .catch((error) => {
                console.error(error);
                logError(error);
            });
    };

    const handleOnProcessedOutsideDelete = async () => {
        const dto: BankTransactionUpdateDto = {
            id: initialBankTransfer.id,
            amountNoninvoice: null,
            noninvoiceNotes: null,
            isNonInvoiceTransaction: initialBankTransfer.isNonInvoiceTransaction,
            invoicePayments: initialBankTransfer.invoicePayments.map((bt) => {
                return {
                    invoicePaymentId: bt.id,
                    invoiceId: bt.invoice.id,
                    amountInTransactionCurrency: bt.amountInTransactionCurrency,
                };
            }),
        };
        await updateBankTransfer(dto)
            .unwrap()
            .then((resp) => {
                onBankTransferUpdated(resp);
                setIsProcessedOutsideBoxDisplayed(false);
            })
            .catch((error) => {
                console.error(error);
                logError(error);
            });
    };

    const onProcessedOutsideChange = async () => {
        await handleSubmit((data) => handleOnProcessedOutsideChange(data))();
    };

    const onNonInvoiceTransactionChange = async () => {
        await handleSubmit((data) => handleOnNonInvoiceTransactionChange(data))();
    };

    const balance = [
        {
            content: (
                <div className="flex flex-row items-center gap-4">
                    <WithTooltip
                        text={isProcessedOutsideDisabled ? t('bank-transfers.disabled-processed-outside') : undefined}
                    >
                        <CheckboxLabel disabled={isProcessedOutsideDisabled || isProcessedOutside || isInViewMode}>
                            <Checkbox
                                variant="secondary"
                                onCheckedChange={(checked) => {
                                    setIsProcessedOutsideBoxDisplayed(checked);
                                }}
                                checked={isProcessedOutside || isProcessedOutsideBoxDisplayed}
                                disabled={isProcessedOutsideDisabled || isProcessedOutside || isInViewMode}
                            />
                            {t('bank-transfers.processed-outside')}
                        </CheckboxLabel>
                    </WithTooltip>
                    <WithTooltip
                        text={
                            isNonInvoiceTransactionDisabled
                                ? t('bank-transfers.disabled-not-related-to-invoice')
                                : undefined
                        }
                    >
                        <CheckboxLabel disabled={isNonInvoiceTransactionDisabled || isInViewMode}>
                            <CheckboxInput
                                control={control}
                                name={'isNonInvoiceTransaction'}
                                onChange={() => {
                                    void onNonInvoiceTransactionChange();
                                }}
                                disabled={isNonInvoiceTransactionDisabled || isInViewMode}
                            />
                            {t('bank-transfers.not-related-to-invoice')}
                        </CheckboxLabel>
                    </WithTooltip>
                </div>
            ),
            label: <></>,
        },
        {
            content: (
                <div className="flex w-full flex-col gap-4">
                    <div className="flex flex-row items-center justify-between">
                        <p className="text-body1 text-fg-secondary">
                            <FormattedMessage id="bank-transfers.amount" />
                        </p>
                        <div className="flex flex-row items-center gap-1">
                            <MoneyDisplay
                                amount={initialBankTransfer.entryAmount}
                                currency={initialBankTransfer.entryAmountCurrencyTypeCode}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        {initialBankTransfer.invoicePayments.map((payment) => {
                            return (
                                <div className="flex flex-row items-start justify-between" key={payment.id}>
                                    <div className="flex flex-row items-center gap-2">
                                        <p className="text-body1 text-fg-secondary">
                                            <FormattedMessage id="bank-transfers.payment-of-invoice" />
                                        </p>
                                        <RecordLink
                                            className="entity-link w-fit no-underline hover:underline"
                                            to={`/invoice/${payment.invoice.id}`}
                                            label={payment.invoice.invoiceNumber}
                                            withOrigin
                                        />
                                    </div>

                                    <div className="flex flex-col items-end">
                                        <MoneyDisplay
                                            negate={true}
                                            amount={payment.amountInTransactionCurrency ?? 0}
                                            currency={payment.transactionCurrencyTypeCode}
                                        />

                                        {payment.currencyTypeCode !== payment.transactionCurrencyTypeCode && (
                                            <span className="text-caption text-fg-secondary">
                                                <MoneyDisplay
                                                    negate={true}
                                                    amount={payment.amount}
                                                    currency={payment.currencyTypeCode}
                                                />
                                                {payment.transactionCurrencyConversionRate ? (
                                                    <>
                                                        {' · '}
                                                        <FormattedMessage id="bank-transfers.conversion-rate" />{' '}
                                                        {String(Number(payment.transactionCurrencyConversionRate))}
                                                        {payment.transactionCurrencyConversionDate ? (
                                                            <>
                                                                {' ('}
                                                                <FormattedDate
                                                                    value={payment.transactionCurrencyConversionDate}
                                                                    fallback=""
                                                                />
                                                                {')'}
                                                            </>
                                                        ) : null}
                                                    </>
                                                ) : null}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {isProcessedOutside && !isProcessedOutsideBoxDisplayed ? (
                            <div className="flex flex-col">
                                <div className="flex flex-row items-center justify-between">
                                    <div className="flex flex-row items-center gap-2">
                                        <p className="text-body1 text-fg-secondary">
                                            {initialBankTransfer.noninvoiceNotes}
                                        </p>
                                        <Button
                                            {...buttonStyle({ variant: 'outlined', size: 'extraSmall' })}
                                            onClick={() => setIsProcessedOutsideBoxDisplayed(true)}
                                            disabled={isInViewMode}
                                        >
                                            <FormattedMessage id="edit" />
                                        </Button>
                                    </div>

                                    <div className="flex flex-row gap-1">
                                        <MoneyDisplay
                                            negate={true}
                                            amount={initialBankTransfer.amountNoninvoice ?? 0}
                                            currency={initialBankTransfer.entryAmountCurrencyTypeCode}
                                        />
                                    </div>
                                </div>
                                <span className="text-caption text-fg-secondary self-end">
                                    <FormattedMessage id="bank-transfers.processed-outside-crespect" />
                                </span>
                            </div>
                        ) : null}
                    </div>
                    {isProcessedOutsideBoxDisplayed ? (
                        <div className="bg-grey-100 w-full rounded-[4px]">
                            <div className="flex flex-col gap-4 p-2">
                                <div className="flex flex-row items-center justify-between">
                                    <p className="text-body1 text-fg-secondary">
                                        <FormattedMessage id="bank-transfers.processed-outside-crespect" />
                                    </p>
                                    <div className="flex flex-row items-center gap-2">
                                        <DecimalInput
                                            gap={0}
                                            inputProps={{
                                                style: { appearance: 'textfield' },
                                                'aria-label': t('bank-transfers.processed-outside-crespect'),
                                            }}
                                            control={control}
                                            name={'amountNoninvoice'}
                                            required
                                            allowNegative
                                            wrapperClassName="w-[120px] [&_input]:bg-paper"
                                        />
                                        <div>{initialBankTransfer.entryAmountCurrencyTypeCode}</div>
                                    </div>
                                </div>
                                <div className="flex flex-col">
                                    <p className="text-body1 text-fg-secondary">
                                        <FormattedMessage id="bank-transfers.explanation" />
                                    </p>
                                    <TextInput
                                        placeholder={'bank-transfers.processed-outside-explanation'}
                                        control={control}
                                        name={'noninvoiceNotes'}
                                        required
                                        inputProps={{ 'aria-label': t('bank-transfers.explanation') }}
                                        wrapperClassName="[&_input]:bg-paper"
                                    />
                                </div>
                                <div className="flex flex-row justify-between">
                                    <Button
                                        {...buttonStyle({ variant: 'outlined', size: 'small' })}
                                        onClick={() => {
                                            if (isProcessedOutside) {
                                                void handleOnProcessedOutsideDelete();
                                            } else {
                                                setIsProcessedOutsideBoxDisplayed(false);
                                            }
                                        }}
                                    >
                                        <FormattedMessage id={isProcessedOutside ? 'delete' : 'cancel'} />
                                    </Button>
                                    <Button
                                        {...buttonStyle({ variant: 'contained', color: 'secondary', size: 'small' })}
                                        onClick={() => {
                                            void onProcessedOutsideChange();
                                        }}
                                    >
                                        <FormattedMessage id={'save'} />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <div className="flex flex-row items-center justify-between">
                        <p className="text-body1 text-fg-secondary">
                            <FormattedMessage id="bank-transfers.remaining" />
                        </p>
                        <div
                            className={cn(
                                'flex flex-row gap-1 font-semibold',
                                initialBankTransfer.remainingAmount < 0 && 'text-fg-error',
                                initialBankTransfer.remainingAmount == 0 && 'text-primary'
                            )}
                        >
                            <MoneyDisplay
                                amount={initialBankTransfer.remainingAmount}
                                currency={initialBankTransfer.entryAmountCurrencyTypeCode}
                            />
                        </div>
                    </div>
                </div>
            ),
            label: <></>,
        },
    ];

    return (
        <>
            <GridItemList titleId="bank-transfers.balance" size={12} data={balance} />
            <div className="new:mt-(--card-gap) mt-6">
                <WithTooltip text={isConnectInvoiceDisabled ? t('bank-transfers.disabled-connect-invoice') : undefined}>
                    <BalanceSection
                        title={t('bank-transfers.connect-invoice')}
                        open={isExpanded}
                        onOpenChange={setIsExpanded}
                        disabled={isConnectInvoiceDisabled || isInViewMode}
                    >
                        <BankTransferConnectInvoices
                            bankTransfer={initialBankTransfer}
                            onBankTransferUpdated={onBankTransferUpdated}
                            onClose={() => setIsExpanded(false)}
                            currentEmployee={currentEmployee}
                        />
                    </BalanceSection>
                </WithTooltip>
            </div>
            <div className="new:mt-(--card-gap) mt-6">
                <BalanceSection
                    title={t('bank-transfers.transaction-notes')}
                    defaultOpen
                    detailsClassName="pt-4"
                    action={
                        <PermissionButton
                            permissions={['UPDATE.BANK_TRANSACTIONS.ALL', 'UPDATE.BANK_TRANSACTIONS.MY_COMPANY']}
                            className="hover:bg-grey-100 hover:border-divider mr-6"
                            variant="outlined"
                            size="extraSmall"
                            onClick={() => {
                                openModal(
                                    <BankTransactionNotesModal
                                        bankTransfer={initialBankTransfer}
                                        onClose={closeModal}
                                        onSubmit={updateBankTransfer}
                                        onBankTransferUpdated={onBankTransferUpdated}
                                    />
                                );
                            }}
                        >
                            <FormattedMessage id="bank-transfers.edit-transaction-notes" />
                        </PermissionButton>
                    }
                >
                    <div className="w-fit min-w-[300px]">
                        {initialBankTransfer.transactionNotes?.split('\n').map((sentence, index, array) => (
                            // oxlint-disable-next-line react-doctor/no-array-index-as-key -- rendering split text lines from immutable string; no stable id available
                            <p key={index} className={cn('text-body1', index !== array.length - 1 && 'mb-2')}>
                                {sentence.trim()}
                            </p>
                        )) || ''}
                    </div>
                </BalanceSection>
            </div>
        </>
    );
};

/** A tooltip only when there is something to say; the wrapper is the hover target, as a disabled control fires none. */
function WithTooltip({ text, children }: { text?: string; children: ReactNode }) {
    return text ? (
        <Tooltip content={text}>
            <div>{children}</div>
        </Tooltip>
    ) : (
        children
    );
}

function CheckboxLabel({ disabled, children }: { disabled?: boolean; children: ReactNode }) {
    return (
        <label
            className={cn(
                'text-body1 flex cursor-pointer items-center gap-2',
                disabled && 'text-fg-disabled cursor-not-allowed'
            )}
        >
            {children}
        </label>
    );
}

interface BalanceSectionProps {
    title: string;
    children: ReactNode;
    /** Sits in the header, beside the chevron, clickable on its own. */
    action?: ReactNode;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    disabled?: boolean;
    detailsClassName?: string;
}

/**
 * A card that folds under its header. The whole header toggles it — the toggle is laid over
 * the row — while an `action` in the header stays clickable on top of it.
 *
 * A folded body stays in the layout, only zero-high and invisible: it keeps a half-filled matching form's
 * state, and it keeps counting towards the width of the pane, so the two panes of the page do not trade
 * width as a section folds.
 */
function BalanceSection({
    title,
    children,
    action,
    open,
    defaultOpen = false,
    onOpenChange,
    disabled,
    detailsClassName,
}: BalanceSectionProps) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const isOpen = open ?? uncontrolledOpen;
    const titleId = useId();
    const panelId = useId();

    const toggle = () => {
        setUncontrolledOpen(!isOpen);
        onOpenChange?.(!isOpen);
    };

    return (
        <div className="border-divider bg-paper new:border-0 new:shadow-card new:outline-hidden rounded-[8px] border">
            <div
                className={cn(
                    'max-figma:px-2 relative flex min-h-[46px] items-center px-4 py-[5px]',
                    disabled && 'opacity-38'
                )}
            >
                <button
                    type="button"
                    aria-labelledby={titleId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    disabled={disabled}
                    onClick={toggle}
                    className="focus-visible:ring-ring/50 absolute inset-0 cursor-pointer rounded-[8px] outline-none focus-visible:ring-3 disabled:cursor-default"
                />
                <span id={titleId} className="my-2.5 ml-2 flex-1 text-base leading-[1.375rem] font-semibold">
                    {title}
                </span>
                {action ? <div className="relative">{action}</div> : null}
                <ExpandMoreIcon
                    className={cn('text-grey-500 transition-transform duration-150', isOpen && 'rotate-90')}
                />
            </div>
            {/* The rows track animates the height; `visibility` flips at the end of a fold and the start of an
                unfold, so the body stays painted while it moves. `overflow-clip` rather than `hidden`: a scroll
                container would stop the body counting towards the pane's width. */}
            <div
                id={panelId}
                className={cn(
                    'grid transition-[grid-template-rows,visibility] duration-250 ease-in-out motion-reduce:transition-none',
                    isOpen ? 'grid-rows-[1fr]' : 'invisible grid-rows-[0fr]'
                )}
            >
                <div className="min-h-0 overflow-clip">
                    <div className={cn('border-divider max-figma:p-4 border-t p-6', detailsClassName)}>{children}</div>
                </div>
            </div>
        </div>
    );
}

export default BankTransferBalance;
